import { CommonModule } from '@angular/common';
import { Component, Inject, inject, signal } from '@angular/core';
import {
  FormBuilder,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { AuthService } from '@app/services/auth.service';
import { FaultType } from '@app/services/fault-report.interface';
import { FaultService } from '@app/services/fault.service';
import { FAULT_TYPES } from '@app/shared/data/fault-types';
import { resetFormState } from '@app/shared/utils/form';
import { catchError, finalize } from 'rxjs';
import { SimplebarAngularModule } from 'simplebar-angular';
import { AlertComponent } from '../alert/alert.component';

export interface ReportFaultDialogData {
  stationId: number;
  chargerId: number;
  connectorId?: number;
  chargerName?: string;
  connectorName?: string;
}

@Component({
  selector: 'app-report-fault-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatFormFieldModule,
    FormsModule,
    MatInputModule,
    ReactiveFormsModule,
    MatSelectModule,
    SimplebarAngularModule,
    AlertComponent,
  ],
  templateUrl: './report-fault-dialog.component.html',
  styleUrl: './report-fault-dialog.component.scss',
})
export class ReportFaultDialogComponent {
  dialogRef = inject(MatDialogRef<ReportFaultDialogComponent>);
  private _formBuilder = inject(FormBuilder);

  formGroup = this._formBuilder.group({
    faultType: ['', Validators.required],
    description: [''],
  });
  isLoading = signal(false);
  photoName = signal<string | null>(null);

  FAULT_TYPES = FAULT_TYPES;
  keepOriginalOrder = () => 0;

  successMessage = '';
  errorMessage = '';

  constructor(
    private faultService: FaultService,
    private authService: AuthService,
    @Inject(MAT_DIALOG_DATA) public props: ReportFaultDialogData,
  ) {
    // Reports are login-only; close if the session drops.
    this.authService.whenNotAuthenticated(() => {
      this.dialogRef.close();
    });
  }

  get contextLabel(): string {
    const parts: string[] = [];
    if (this.props.chargerName) parts.push(this.props.chargerName);
    if (this.props.connectorName) {
      parts.push(`${this.props.connectorName} connector`);
    }
    return parts.join(' · ');
  }

  close() {
    this.dialogRef.close();
  }

  onPhotoSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    // TODO: upload via the FileService signed-URL flow and set photoUrl on submit.
    this.photoName.set(file ? file.name : null);
  }

  onSubmit() {
    if (!this.formGroup.valid) return;

    const reporterUserId = this.authService.userSignal()?.uid;
    if (!reporterUserId) {
      this.dialogRef.close();
      return;
    }

    const value = this.formGroup.value;
    this.isLoading.set(true);

    this.faultService
      .reportFault({
        stationId: this.props.stationId,
        chargerId: this.props.chargerId,
        connectorId: this.props.connectorId,
        faultType: value.faultType as FaultType,
        description: value.description || undefined,
        reporterUserId,
      })
      .pipe(
        catchError(() => {
          this.errorMessage = 'Error submitting report';
          throw new Error('Error submitting report');
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
      )
      .subscribe(() => {
        this.successMessage = 'Report submitted';
        this.errorMessage = '';
        resetFormState(this.formGroup);
        this.photoName.set(null);
      });
  }
}
